/**
 * @file CustomerOverviewTab.tsx
 * @description «Обзор» карточки клиента — рабочая сводка менеджера
 * (phase19.31, SC68): без ленты (она на своей вкладке).
 *
 * Раскладка:
 *  - от 1024 — две колонки: слева «Сейчас в работе», «Задачи по клиенту»,
 *    «Заметки о клиенте»; справа «Требует внимания», «Связь», «Как покупает»;
 *  - уже — один столбец в порядке важности: «Требует внимания», «Сейчас в
 *    работе», «Задачи», «Связь», «Заметки», «Как покупает».
 * Колонки на телефоне растворяются (`contents`), порядок задаёт `order-*` —
 * блоки не дублируются в разметке.
 *
 * Блок, на который у сотрудника нет права (`overview.access`: заказы, задачи
 * и заметки, переписка), не показывается. «Требует внимания» — только когда
 * не пусто. Сбой сводки —
 * «Не удалось загрузить сводку» и «Повторить»; повтор не прячет прежние данные.
 */

import { OverviewBuyingSection } from "./OverviewBuyingSection";
import { OverviewContactSection } from "./OverviewContactSection";
import { OverviewNotesSection } from "./OverviewNotesSection";
import { OverviewOpenOrdersSection } from "./OverviewOpenOrdersSection";
import { OverviewTasksSection } from "./OverviewTasksSection";
import { attentionItems, type OverviewLinks } from "./overview-view";
import { AttentionList } from "../../ui/AttentionList";
import { ListError, ListSkeleton } from "../../ui/ListStates";
import { SectionCard } from "../../ui/SectionCard";
import type { CustomerOverviewState } from "../card/useCustomerOverview";

interface Props {
  state: CustomerOverviewState;
  links: OverviewLinks;
  /** Право работать с CRM: флажки задач, «+ Задача», «+ Заметка». */
  canUseCrm: boolean;
  /** Право на переписку: кнопка «Написать» в «Связи». */
  canChat: boolean;
  onAddTask: () => void;
  onAddNote: () => void;
}

const COLUMN = "contents lg:flex lg:min-w-0 lg:flex-col lg:gap-4";

export function CustomerOverviewTab({ state, links, canUseCrm, canChat, onAddTask, onAddNote }: Props) {
  const { overview } = state;

  if (!overview) {
    return state.loading ? (
      <ListSkeleton rows={4} compact />
    ) : (
      <ListError message={state.error ?? "Не удалось загрузить сводку"} onRetry={state.refresh} />
    );
  }

  const attention = attentionItems(overview.attention, links);
  const { access } = overview;
  const crm = canUseCrm && access.tasks;

  return (
    <div className="flex flex-col gap-4">
      {state.error ? <ListError message={state.error} onRetry={state.refresh} /> : null}
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-12 lg:items-start">
        <div className={`${COLUMN} lg:col-span-7`}>
          {access.orders ? (
            <div className="order-2 min-w-0">
              <OverviewOpenOrdersSection orders={overview.openOrders} total={overview.openOrdersTotal} orderHref={links.order} />
            </div>
          ) : null}
          {access.tasks ? (
            <div className="order-3 min-w-0">
              <OverviewTasksSection
                tasks={overview.tasks}
                total={overview.tasksTotal}
                completing={state.completing}
                onComplete={crm ? state.completeTask : undefined}
                onAdd={crm ? onAddTask : undefined}
              />
            </div>
          ) : null}
          {access.tasks ? (
            <div className="order-5 min-w-0">
              <OverviewNotesSection notes={overview.notes} total={overview.notesTotal} onAdd={crm ? onAddNote : undefined} />
            </div>
          ) : null}
        </div>
        <div className={`${COLUMN} lg:col-span-5`}>
          {attention.length > 0 ? (
            <div className="order-1 min-w-0">
              <SectionCard title="Требует внимания">
                <AttentionList aria-label="Что требует внимания по клиенту" items={attention} />
              </SectionCard>
            </div>
          ) : null}
          <div className="order-4 min-w-0">
            <OverviewContactSection
              contact={overview.contact}
              marketingConsent={overview.marketingConsent}
              chatHref={canChat && access.chat ? links.chat(overview.contact?.conversationId) : undefined}
            />
          </div>
          <div className="order-6 min-w-0">
            <OverviewBuyingSection buying={overview.buying} />
          </div>
        </div>
      </div>
    </div>
  );
}
